import { useState } from 'react';
import { Button, Modal } from '../ui';

const DISPUTE_REASONS = {
  notAsDescribed: 'Item not as described',
  notReceived: 'Item not received / winner no-show',
  sellerUnresponsive: 'Seller is unresponsive',
  winnerUnresponsive: 'Winner is unresponsive',
  damaged: 'Item was damaged',
};

const STATUS_CONFIG = {
  open: {
    label: 'Awaiting Response',
    color: 'text-accent',
    bgColor: 'bg-accent/10',
    borderColor: 'border-accent/30',
    icon: '!',
  },
  underReview: {
    label: 'Under Review',
    color: 'text-primary',
    bgColor: 'bg-primary/10',
    borderColor: 'border-primary/30',
    icon: '...',
  },
  resolved: {
    label: 'Resolved',
    color: 'text-success',
    bgColor: 'bg-success/10',
    borderColor: 'border-success/30',
    icon: '✓',
  },
};

const RESOLUTION_LABELS = {
  buyerFavour: 'Resolved in winner\'s favour',
  sellerFavour: 'Resolved in seller\'s favour',
  split: 'Split decision',
};

/**
 * Evidence Gallery Component
 *
 * Displays evidence images with click-to-view functionality.
 */
function EvidenceGallery({ evidence = [], label }) {
  const [viewingImage, setViewingImage] = useState(null);

  if (!evidence || evidence.length === 0) return null;

  return (
    <>
      <div className="mt-3">
        <p className="text-cream/40 text-xs uppercase tracking-wide mb-2">{label}</p>
        <div className="flex flex-wrap gap-2">
          {evidence.map((url, idx) => (
            <button
              key={url}
              onClick={() => setViewingImage(url)}
              className="relative group rounded-lg overflow-hidden border border-cream/10 hover:border-primary/50 transition-colors"
            >
              <img
                src={url}
                alt={`Evidence ${idx + 1}`}
                className="w-14 h-14 object-cover"
              />
              <div className="absolute inset-0 bg-midnight/0 group-hover:bg-midnight/20 transition-colors flex items-center justify-center">
                <span className="opacity-0 group-hover:opacity-100 text-white text-lg transition-opacity">
                  <ViewIcon className="w-4 h-4" />
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Full-size image modal */}
      <Modal
        isOpen={!!viewingImage}
        onClose={() => setViewingImage(null)}
        title="Evidence"
        dark
        size="lg"
      >
        <div className="flex items-center justify-center">
          <img
            src={viewingImage}
            alt="Evidence"
            className="max-w-full max-h-[70vh] object-contain rounded-lg"
          />
        </div>
      </Modal>
    </>
  );
}

/**
 * DisputeStatus Component
 *
 * Shows the current state of a dispute with timeline and actions.
 * Follows RAFLR_UI design system (dark-first, teal/amber accents).
 */
export default function DisputeStatus({
  dispute,
  isRaiser,
  needsResponse,
  onRespond,
}) {
  if (!dispute) return null;

  const status = STATUS_CONFIG[dispute.status] || STATUS_CONFIG.open;
  const reasonLabel = DISPUTE_REASONS[dispute.reason] || dispute.reason;

  return (
    <div className={`rounded-xl border ${status.borderColor} bg-midnight-light p-5`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className={`w-8 h-8 rounded-full ${status.bgColor} ${status.color} flex items-center justify-center font-mono font-bold text-sm`}>
            {status.icon}
          </span>
          <h3 className="font-display font-semibold text-cream">
            Dispute {status.label}
          </h3>
        </div>
        <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${status.bgColor} ${status.color}`}>
          {status.label}
        </span>
      </div>

      {/* Reason */}
      <div className="mb-4">
        <p className="text-cream/50 text-xs font-bold uppercase tracking-wider mb-1">
          Issue Reported
        </p>
        <p className="text-cream font-medium">{reasonLabel}</p>
      </div>

      {/* Description */}
      <div className="bg-midnight rounded-lg p-4 mb-4 border border-midnight-lighter">
        <p className="text-cream/50 text-xs font-bold uppercase tracking-wider mb-2">
          {isRaiser ? 'Your Report' : 'Their Report'}
        </p>
        <p className="text-cream/80 text-sm leading-relaxed">{dispute.description}</p>

        {/* Evidence from initial report */}
        <EvidenceGallery
          evidence={dispute.evidence}
          label="Attached evidence"
        />
      </div>

      {/* Response (if any) */}
      {dispute.responseDescription && (
        <div className="bg-midnight rounded-lg p-4 mb-4 border border-midnight-lighter">
          <p className="text-cream/50 text-xs font-bold uppercase tracking-wider mb-2">
            {isRaiser ? 'Their Response' : 'Your Response'}
          </p>
          <p className="text-cream/80 text-sm leading-relaxed">{dispute.responseDescription}</p>

          {/* Evidence from response */}
          <EvidenceGallery
            evidence={dispute.responseEvidence}
            label="Response evidence"
          />
        </div>
      )}

      {/* Resolution (if resolved) */}
      {dispute.status === 'resolved' && dispute.resolution && (
        <div className="bg-success/10 rounded-lg p-4 border border-success/30">
          <p className="text-cream/50 text-xs font-bold uppercase tracking-wider mb-1">
            Resolution
          </p>
          <p className={`font-semibold ${status.color}`}>
            {RESOLUTION_LABELS[dispute.resolution] || dispute.resolution}
          </p>
          {dispute.adminNotes && (
            <p className="text-cream/70 text-sm mt-2">{dispute.adminNotes}</p>
          )}
        </div>
      )}

      {/* Action: Respond button */}
      {needsResponse && (
        <div className="mt-5 pt-4 border-t border-midnight-lighter">
          <p className="text-cream/70 text-sm mb-4">
            A dispute has been raised against you. Please respond with your side of the story.
          </p>
          <Button onClick={onRespond} variant="primary">
            Respond to Dispute
          </Button>
        </div>
      )}

      {/* Waiting message */}
      {dispute.status === 'open' && isRaiser && (
        <div className="mt-4 pt-4 border-t border-midnight-lighter">
          <p className="text-cream/50 text-sm">
            Waiting for the other party to respond. You'll be notified when they do.
          </p>
        </div>
      )}

      {dispute.status === 'underReview' && (
        <div className="mt-4 pt-4 border-t border-midnight-lighter">
          <p className="text-cream/50 text-sm">
            An admin is reviewing this dispute. You'll be notified when a decision is made.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Compact dispute banner for showing in headers/cards
 */
export function DisputeBanner({ dispute, onClick }) {
  if (!dispute) return null;

  const status = STATUS_CONFIG[dispute.status] || STATUS_CONFIG.open;

  return (
    <button
      onClick={onClick}
      className={`w-full rounded-lg border ${status.borderColor} bg-midnight-light p-4 text-left transition-all hover:border-opacity-60`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`font-mono font-bold ${status.color}`}>{status.icon}</span>
          <span className="text-cream font-medium text-sm">Dispute {status.label}</span>
        </div>
        <span className="text-cream/40 text-xs">View details →</span>
      </div>
    </button>
  );
}

// Icons
function ViewIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
      />
    </svg>
  );
}
